import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { authClient, useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import { Building2, Loader2, CheckCircle2, XCircle, LogIn } from "lucide-react";

type InvitationStatus = "loading" | "fetching" | "ready" | "accepting" | "success" | "error" | "not-found";

type InvitationData = {
  id: string;
  email: string;
  organizationName: string;
  organizationSlug: string;
  inviterName: string;
  role: string;
  status: string;
  expiresAt: Date;
};

export function AcceptInvitation() {
  const navigate = useNavigate();
  const { invitationId } = useParams<{ invitationId: string }>();
  const { data: session, isPending: sessionPending } = useSession();

  const [status, setStatus] = useState<InvitationStatus>("loading");
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch invitation details via tRPC
  const {
    data: trpcInvitation,
    isLoading: invitationLoading,
    error: invitationError,
  } = trpc.organization.getInvitation.useQuery(
    { invitationId: invitationId! },
    { enabled: !!session && !!invitationId }
  );

  useEffect(() => {
    if (invitationLoading) {
      setStatus("fetching");
    } else if (invitationError) {
      setError(invitationError.message || "Failed to fetch invitation");
      setStatus("not-found");
    } else if (trpcInvitation) {
      setInvitation({
        id: trpcInvitation.id,
        email: trpcInvitation.email,
        organizationName: trpcInvitation.organizationName,
        organizationSlug: trpcInvitation.organizationSlug,
        inviterName: trpcInvitation.inviterName || trpcInvitation.inviterEmail || "Someone",
        role: trpcInvitation.role,
        status: trpcInvitation.status,
        expiresAt: new Date(trpcInvitation.expiresAt),
      });
      setStatus("ready");
    }
  }, [invitationLoading, invitationError, trpcInvitation]);

  const handleAccept = async () => {
    if (!invitationId) return;

    setStatus("accepting");
    setError(null);

    try {
      const { data, error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId,
      });

      if (acceptError) {
        setError(acceptError.message || "Failed to accept invitation");
        setStatus("error");
        return;
      }

      setStatus("success");

      // Redirect to the organization page after a short delay
      setTimeout(() => {
        navigate(`/organizations/${invitation?.organizationSlug || ""}`);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
      setStatus("error");
    }
  };

  const handleReject = async () => {
    if (!invitationId) return;

    try {
      await authClient.organization.rejectInvitation({
        invitationId,
      });
      navigate("/organizations");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject invitation");
    }
  };

  // Not logged in state
  if (!sessionPending && !session) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-neon-green/10 flex items-center justify-center mb-4">
              <LogIn className="h-6 w-6 text-neon-green" />
            </div>
            <CardTitle className="text-silica">Sign In Required</CardTitle>
            <CardDescription>
              Please sign in to view and accept this invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to={`/login?redirect=/accept-invitation/${invitationId}`}>
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (status === "loading" || status === "fetching") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-neon-green" />
            <p className="text-chromium-mist">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not found state
  if (status === "not-found") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
              <XCircle className="h-6 w-6 text-red-400" />
            </div>
            <CardTitle className="text-silica">Invitation Not Found</CardTitle>
            <CardDescription>
              {error || "This invitation may have expired or already been used."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/organizations">
                Go to Organizations
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-neon-green/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-neon-green" />
            </div>
            <CardTitle className="text-silica">Welcome!</CardTitle>
            <CardDescription>
              You've joined {invitation?.organizationName}. Redirecting you now...
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-chromium-mist" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
              <XCircle className="h-6 w-6 text-red-400" />
            </div>
            <CardTitle className="text-silica">Something Went Wrong</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={handleAccept} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ready state - show invitation details
  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-neon-green/10 flex items-center justify-center mb-4">
            <Building2 className="h-6 w-6 text-neon-green" />
          </div>
          <CardTitle className="text-silica">You're Invited!</CardTitle>
          <CardDescription>
            {invitation?.inviterName} has invited you to join
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-silica mb-1">
              {invitation?.organizationName}
            </h3>
            <p className="text-chromium-mist text-sm">
              Role: <span className="capitalize">{invitation?.role}</span>
            </p>
          </div>

          {invitation?.status !== "pending" && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
              <p className="text-sm text-yellow-400">
                This invitation has already been {invitation?.status}.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleAccept}
              className="w-full"
              disabled={status === "accepting" || invitation?.status !== "pending"}
            >
              {status === "accepting" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept Invitation
                </>
              )}
            </Button>
            <Button
              onClick={handleReject}
              variant="outline"
              className="w-full"
              disabled={status === "accepting" || invitation?.status !== "pending"}
            >
              Decline
            </Button>
          </div>

          <p className="text-xs text-chromium-mist text-center">
            Invitation expires {invitation?.expiresAt.toLocaleDateString()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
